import { useCallback, useEffect, useState } from "react";

import { Button, Field, FullScreenLayer, Icons, LoadingState, LockBadge, SegmentedControl, TextInput, useFeedback } from "../../../../../ds";
import { PageEditor, useEditorPreference, useResolvedPageFormat } from "../../../../../astromark";
import type { PageEditorKind } from "../../../../../astromark";
import { PageContentChangedEvent } from "../../../../../common/events/PageContentChangedEvent";
import { useServiceCallPro } from "../../../../../common/services/UseServiceCall";
import { LockMark } from "../../../../locks/models/LockDTO";
import { usePageMetadata } from "../../../hooks/usePageMetadata";
import { EditPageRequestFromPopupDTO } from "../../../models/services/EditPageRequestFromPopupDTO";
import { PageService } from "../../../services/PageService";

const pagesSvc = new PageService();

export const TitleRequiredText = "A page needs a title.";

/** The head's editor switch: Auto (the editor of the page's format), the legacy Markdown editor, the AstroMark editor. */
const EDITOR_OPTIONS: { value: PageEditorKind; label: string }[] = [
    { value: "auto", label: "Auto" },
    { value: "legacy", label: "Markdown" },
    { value: "astromark", label: "AstroMark" },
];

interface IEditorCommon {
    spaceId: string;
    onClose: () => void;
    /** "Space › Parent" above the title (eyebrow). */
    trail?: string[];
    /** The edited page's lock (a badge in the head). */
    lockMark?: LockMark;
}

export interface IAddPageEditorProps extends IEditorCommon {
    mode: "add";
    /** Creates a child of this page; a root page without it. */
    parentPageId?: string;
    onCompleted: (newPageId: number) => void;
}

export interface IEditPageEditorProps extends IEditorCommon {
    mode: "edit";
    pageId: string;
    onSave: () => void;
}

export type IPageEditorPopupProps = IAddPageEditorProps | IEditPageEditorProps;

/**
 * Add Page / Edit Page (board "Ark-Editor"): a full-screen layer with the Title field, the page editor (`PageEditor` from
 * `src/astromark`: the markdown source and a live preview rendered like the reader) and Cancel / Save. Saving writes to the
 * space directly (no change request). Uploads go to the page's attachments when the page exists.
 */
export function PageEditorPopup(props: IPageEditorPopupProps) {
    const feedback = useFeedback();
    const { spaceId, onClose } = props;
    const pageId = props.mode === "edit" ? props.pageId : undefined;

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [isLoadingData, setIsLoadingData] = useState(props.mode === "edit");
    const [titleError, setTitleError] = useState<string>();

    // the dialect: the page's own (edit) or the one its parent's subtree is in (add)
    const metadata = usePageMetadata(props.mode === "edit" ? props.pageId : props.parentPageId);
    const format = useResolvedPageFormat(metadata);
    // which editor: the user's choice, kept per browser (`ark.pageEditor`); `PageEditor` follows it
    const [editorKind, setEditorKind] = useEditorPreference();

    const addPageCall = useServiceCallPro(pagesSvc.createPage);
    const editPageCall = useServiceCallPro(pagesSvc.editPage);
    const isSaving = addPageCall.isLoading || editPageCall.isLoading;

    /****************************
     * DATA REQUESTS
     *****************************/

    useEffect(() => {
        if (!spaceId || !pageId) return;
        pagesSvc
            .getPageById(spaceId, pageId)
            .then((result) => {
                setContent(result?.pageContent || "");
                setTitle(result.title);
                setIsLoadingData(false);
            })
            .catch((error) => {
                setIsLoadingData(false);
                feedback.error(error?.code ?? error, "Error!");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaceId, pageId]);

    /****************************
     * USER ACTIONS
     *****************************/

    const handleSave = useCallback(() => {
        if (!title.trim()) {
            setTitleError(TitleRequiredText);
            return;
        }
        if (!spaceId) return;

        if (props.mode === "add") {
            const { parentPageId, onCompleted } = props;
            addPageCall
                .invoke(title, content, spaceId, parentPageId)
                .then((res) => {
                    onCompleted(res.pageId);
                    feedback.success(`You have created a Page successfully with title: ${res.title}`, "Success!");
                    onClose();
                })
                .catch((err) => feedback.error(err?.code ?? err, "Error!"));
            return;
        }

        const { onSave } = props;
        const request: EditPageRequestFromPopupDTO = { title, content, spaceId, pageId: props.pageId };
        editPageCall
            .invoke(request)
            .then(() => {
                onSave();
                feedback.success("You have edited a Page successfully!", "Success!");
                PageContentChangedEvent.firePageChanged();
                onClose();
            })
            .catch((err) => feedback.error(err?.code ?? err, "Error!"));
    }, [title, content, spaceId, props, addPageCall.invoke, editPageCall.invoke, feedback, onClose]);

    /****************************
     * HTML
     *****************************/

    const eyebrow = (
        <span className="ark-editor-eyebrow">
            <span>{props.mode === "edit" ? "Edit page" : "New page"}</span>
            {props.trail && props.trail.length ? <span className="ark-editor-trail">{props.trail.join(" › ")}</span> : null}
            {props.lockMark ? <LockBadge state={props.lockMark.own ? "own" : "inherited"} by={props.lockMark.lockTitle} /> : null}
        </span>
    );

    return (
        <FullScreenLayer
            className="ark-editor"
            flush
            eyebrow={eyebrow}
            title={props.mode === "edit" ? (title || "Edit page") : "Add page"}
            closeLabel="Close the editor"
            onClose={onClose}
            actions={<SegmentedControl<PageEditorKind> size="sm" aria-label="Editor" options={EDITOR_OPTIONS} value={editorKind} onChange={setEditorKind} />}
        >
            {isLoadingData ? (
                <LoadingState text="Loading the page…" />
            ) : (
                <div className="ark-editor-body">
                    <Field label="Title" required error={titleError}>
                        <TextInput
                            value={title}
                            onChange={(value) => {
                                setTitle(value);
                                if (titleError && value.trim()) setTitleError(undefined);
                            }}
                            placeholder="Insert a title"
                            hasError={!!titleError}
                            autoFocus={props.mode === "add"}
                        />
                    </Field>
                    <div className="ark-editor-surface">
                        <PageEditor
                            value={content}
                            onChange={setContent}
                            format={format}
                            attachTo={pageId ? { spaceId, pageId } : undefined}
                            previewClassName="ark-prose"
                            ariaLabel="Page source"
                            onError={(error) => feedback.error(error, "Error!")}
                        />
                    </div>
                    <div className="ark-editor-foot">
                        <p className="ark-editor-note">
                            <Icons.info size={14} />
                            Saving writes directly to the space; it does not open a change request.
                        </p>
                        <div className="ark-editor-foot-actions">
                            <Button type="secondary" text="Cancel" onClick={onClose} />
                            <Button type="primary" text="Save" icon={<Icons.check size={16} />} isBusy={isSaving} isDisabled={isLoadingData} onClick={handleSave} />
                        </div>
                    </div>
                </div>
            )}
        </FullScreenLayer>
    );
}
